import React, { useState, useEffect } from "react";
import { beepAndVibrate } from "../utils/feedback";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, AlertCircle, Copy, Loader2, Pencil, RotateCcw } from "lucide-react";
import BarcodeScanner from "../components/BarcodeScanner";
import mmsBlisterImg from "../assets/mms-blister.svg";
import mmsBottleImg from "../assets/mms-bottle.svg";
import qrUnknownImg from "../assets/qr-unknown.svg";
import toast from "react-hot-toast";
import { apiService } from "../services/apiService";
import { resolveBarcodeRoute } from "../utils/barcodeRouting";
import { resolveNamaIbu } from "../utils/motherName";

const BarcodeScannerScreen = () => {
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showScanner, setShowScanner] = useState(true); // Start with scanner active
  const [loading, setLoading] = useState(false);
  const [manualNameMother, setManualNameMother] = useState("");
  const [confirmedNameMother, setConfirmedNameMother] = useState("");
  const navigate = useNavigate();
  const nameMother = resolveNamaIbu({
    nameMother: result?.name_mother,
    confirmedNameMother,
  });

  // Auto-start scanning when component mounts
  useEffect(() => {
    setShowScanner(true);
    setError(null);
    setResult(null);
  }, []);

  const handleScanResult = async (barcode) => {
    beepAndVibrate();
    const routeInfo = resolveBarcodeRoute(barcode);

    // Set initial result
    setResult({
      barcode_id: barcode,
      count_type: routeInfo?.countType ?? null,
      barcode_type: routeInfo?.barcodeType ?? null,
      product_name: routeInfo?.productName ?? null,
      detection_route: routeInfo?.detectionRoute ?? null,
      action_label: routeInfo?.actionLabel ?? null,
      mother_name: "Scanned Barcode",
      supplement_id: "N/A",
      name_mother: null,
      id_ibu: null,
    });
    setShowScanner(false);

    if (!routeInfo) {
      toast.error("QR tidak dikenali");
      return;
    }
    toast.success("Barcode scanned successfully!");

    // Fetch additional info from API including name_mother and id_ibu
    setLoading(true);
    try {
      const scanData = await apiService.scanBarcode(barcode);
      setResult((prevResult) => ({
        ...(prevResult || {}),
        mother_name: scanData?.mother_name || prevResult?.mother_name || "Scanned Barcode",
        supplement_id: scanData?.supplement_id || prevResult?.supplement_id || "N/A",
        name_mother: scanData?.name_mother || null,
        id_ibu: scanData?.id_ibu || scanData?.respondent_id || null,
      }));
    } catch (error) {
      console.error("Error fetching barcode details:", error);
      // Don't show error to user, just log it - barcode is already scanned
      // The name_mother and id_ibu will remain null if API call fails
    } finally {
      setLoading(false);
    }
  };

  const handleScannerError = (errorMessage) => {
    setError(errorMessage);
  };

  const resetScanner = () => {
    setResult(null);
    setError(null);
    setManualNameMother("");
    setConfirmedNameMother("");
    setShowScanner(true); // Start scanner again for new scan
  };

  const handleDetectionNavigation = () => {
    if (result?.detection_route) {
      navigate(result.detection_route, {
        state: {
          barcode: result.barcode_id,
          barcodeType: result.barcode_type,
          productName: result.product_name,
          nameMother,
          idIbu: result.id_ibu || null,
        },
      });
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <button
              onClick={() => navigate("/")}
              className="mr-4 p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              Scan Barcode
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Scanner Component */}
        {showScanner && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                MMS Barcode Detection
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                Point your camera at MMS barcode or upload the MMS QR Code from file
              </p>
            </div>

            <BarcodeScanner
              onScanResult={handleScanResult}
              onError={handleScannerError}
              className="max-w-2xl mx-auto"
            />
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mt-4 max-w-2xl mx-auto bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-400 dark:text-red-500 mr-2" />
              <p className="text-red-800 dark:text-red-200">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
            >
              Try again
            </button>
          </div>
        )}

        {/* Result Display */}
        {result && (
          <div className="max-w-md mx-auto">
            <div
              className={`flex flex-col items-center text-center ${
                result.count_type
                  ? ""
                  : "mt-8 mx-auto max-w-xs rounded-xl bg-white dark:bg-gray-800 shadow-md px-6 pt-8 pb-14"
              }`}
            >
              {result.count_type ? (
                (result.count_type === "bottle" || result.count_type === "blister") && (
                  <img
                    src={result.count_type === "bottle" ? mmsBottleImg : mmsBlisterImg}
                    alt=""
                    className="h-36 w-auto mb-8"
                  />
                )
              ) : (
                <img src={qrUnknownImg} alt="" className="h-24 w-24 mb-6 dark:invert" />
              )}
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {result.count_type ? `${result.product_name} Detected` : "QR Tidak Dikenali"}
              </h2>
              <div className="mt-1 flex items-center gap-2">
                <span className="font-mono text-gray-900 dark:text-white break-all">
                  {result.barcode_id}
                </span>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(result.barcode_id);
                      toast.success("Barcode copied to clipboard!");
                    } catch (error) {
                      toast.error("Failed to copy barcode");
                    }
                  }}
                  className="p-1 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                  aria-label="Copy barcode"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Mother's Name */}
            {result.count_type && (
              <div className="mt-12 rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-5">
                <p className="text-gray-900 dark:text-gray-200">Nama Ibu pemilik MMS</p>
                {loading ? (
                  <div role="status" className="mt-2 flex items-center gap-2 text-sm text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Mencari data ibu...
                  </div>
                ) : nameMother ? (
                  <div className="mt-1 flex items-center justify-between gap-4">
                    <p className="text-xl font-bold text-gray-900 dark:text-white break-words">{nameMother}</p>
                    {/* Only a manually saved name is editable; API data stays as is */}
                    {!result.name_mother && (
                      <button
                        onClick={() => {
                          setManualNameMother(confirmedNameMother);
                          setConfirmedNameMother("");
                        }}
                        className="flex items-center gap-1 p-1 text-slate-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                        aria-label="Edit nama ibu"
                      >
                        <Pencil className="h-4 w-4" />
                        Edit
                      </button>
                    )}
                  </div>
                ) : (
                  <form
                    className="mt-4 space-y-3"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (manualNameMother.trim()) {
                        setConfirmedNameMother(manualNameMother.trim());
                      }
                    }}
                  >
                    <input
                      type="text"
                      value={manualNameMother}
                      onChange={(e) => setManualNameMother(e.target.value)}
                      placeholder="Masukan nama ibu"
                      aria-label="Nama ibu"
                      className="w-full px-4 py-3 border border-slate-400 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder:italic placeholder-slate-400 focus:outline-none focus:border-blue-600"
                    />
                    <button
                      type="submit"
                      disabled={!manualNameMother.trim()}
                      className="w-full py-3 rounded-lg bg-slate-100 dark:bg-gray-700 text-slate-500 dark:text-gray-300 enabled:text-slate-700 enabled:hover:bg-slate-200 disabled:cursor-not-allowed"
                    >
                      Simpan nama ibu
                    </button>
                  </form>
                )}
              </div>
            )}

            <button
              onClick={resetScanner}
              className={`mx-auto ${result.count_type ? "mt-6" : "mt-20"} flex items-center gap-2 py-2 text-lg text-gray-900 dark:text-white`}
            >
              <RotateCcw className="h-5 w-5" />
              Scan Ulang
            </button>

            {result.count_type && (
              <button
                onClick={handleDetectionNavigation}
                disabled={!nameMother || !result.detection_route}
                className="mt-8 w-full py-4 rounded-lg bg-blue-600 text-lg text-white hover:bg-blue-700 disabled:bg-blue-600/30 disabled:cursor-not-allowed"
              >
                Next
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BarcodeScannerScreen;
